import CardContainer from '../../../CardContainer/CardContainer';

import { Alert, Box, Button, Stack, Theme, Typography } from '@mui/material';
import { useState } from 'react';
import { validationRules } from '@verifime/utils';
import {
  TFormFieldsAndRules as TFormFieldsAndRules_New,
  getFieldsRules as getFieldsRules_New,
} from '../../../Form/FormGenerator/common/utils';
import { RenderType } from '../../../Form/FormGenerator/utils';
import GenerateFormFieldsNew from '../../../Form/FormGenerator/v2/GenerateFormFields';
import { useCustomForm } from '../../../Form/useCustomForm';

import { OrganisationCodeSelector } from '../OrganisationCodeSelector';

const formFieldsAndRules: TFormFieldsAndRules_New<keyof TFormData> = {
  row1: [
    {
      fieldName: 'returnUrl',
      label: 'Return URL',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_URL_RULES,
      helpText: 'Must start with http:// or https://',
    },
  ],
};

type TFormData = {
  returnUrl: string;
};

export interface OrganisationCodeReturnPathProps {
  organisationCodes: string[];
  selectedCode?: string;
  initialReturnUrl?: string;
  onSave?: (code: string, url: string) => Promise<void> | void;
  onCodeChange?: (code: string) => void;
  dataTestId?: string;
}

export default function OrganisationCodeReturnPath({
  organisationCodes,
  selectedCode = '',
  initialReturnUrl = '',
  onSave,
  onCodeChange,
  dataTestId,
}: Readonly<OrganisationCodeReturnPathProps>) {
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useCustomForm<TFormData>({
    schema: getFieldsRules_New(formFieldsAndRules),
    defaultValues: {
      returnUrl: initialReturnUrl,
    },
  });

  const handleSave = handleSubmit(async (formData) => {
    if (!selectedCode) {
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave?.(selectedCode, formData.returnUrl);
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <CardContainer data-testid={dataTestId} elevation={2}>
      <Typography variant="h6" sx={{ marginBottom: (theme: Theme) => theme.semantic.spacing.base }}>
        Configure Return Path
      </Typography>

      {organisationCodes.length === 0 && (
        <Alert severity="error">
          No organisation codes available. This may indicate a data issue. Please contact the
          development team for assistance.
        </Alert>
      )}

      {organisationCodes.length > 0 && (
        <>
          <OrganisationCodeSelector
            codes={organisationCodes}
            selectedCode={selectedCode}
            onCodeChange={onCodeChange}
            dataTestId={`${dataTestId}-code-selector`}
          />
          <Stack sx={{ marginBottom: (theme: Theme) => theme.semantic.spacing.base }}>
            <GenerateFormFieldsNew
              formFieldsAndRules={formFieldsAndRules}
              control={control}
              errors={errors}
            />
            <Box textAlign="right">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!selectedCode || Boolean(errors.returnUrl) || isSaving}
                data-action="save-return-path"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Stack>
        </>
      )}
    </CardContainer>
  );
}
